import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  RefreshCw, 
  Plus, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

const Sources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  
  // Form State
  const [newSource, setNewSource] = useState({ name: '', type: 'sam_gov', api_key: '' });

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sources/`);
      setSources(res.data);
    } catch (err) {
      console.error("Failed to fetch sources");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name) return;
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sources/add`, newSource);
      toast.success("Source Added. Initial sync started...");
      setShowAdd(false);
      setNewSource({ name: '', type: 'sam_gov', api_key: '' });
      fetchSources();
    } catch (err) {
      toast.error("Failed to add source");
    }
  };

  const handleSync = async (id) => {
    setSyncing(id);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sources/${id}/sync`);
      toast.info("Sync started in background");
      // Simulate refresh
      setTimeout(() => {
        fetchSources();
        setSyncing(null);
      }, 2000);
    } catch (err) {
      toast.error("Sync failed");
      setSyncing(null);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-bold tracking-tight text-foreground font-heading">Tender Sources</h1>
          <p className="text-muted-foreground text-lg">
            Connect external APIs to auto-fetch tenders into your dashboard.
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Add Source
        </Button>
      </div>

      <div className="grid gap-6">
        {sources.map((source) => (
          <Card key={source.id} className="bg-card border-border hover:border-primary/20 transition-all">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">{source.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="outline" className="text-xs">{source.type}</Badge>
                    <span>• Last sync: {source.last_sync ? new Date(source.last_sync).toLocaleString() : 'Never'}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {source.status === 'active' && <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20 border-0">Active</Badge>}
                {source.status === 'error' && <Badge className="bg-red-500/10 text-red-600 hover:bg-red-500/20 border-0">Error</Badge>}
                {source.status === 'pending' && <Badge className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border-0">Pending</Badge>}
                
                <Button variant="outline" size="icon" onClick={() => handleSync(source.id)} disabled={syncing === source.id}>
                  <RefreshCw className={`h-4 w-4 ${syncing === source.id ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {sources.length === 0 && !loading && (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
            <Globe className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground">No Sources Connected</h3>
            <p className="text-sm text-muted-foreground mb-4">Add a tender portal API (like SAM.gov) to get started.</p>
            <Button variant="outline" onClick={() => setShowAdd(true)}>Connect API</Button>
          </div>
        )}
      </div>

      {/* Add Source Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Tender Source</DialogTitle>
            <DialogDescription>Enter your API credentials for the tender portal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Source Name</label>
              <Input 
                placeholder="e.g., SAM.gov Feed" 
                value={newSource.name}
                onChange={(e) => setNewSource({...newSource, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Source Type</label>
              <select 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={newSource.type}
                onChange={(e) => setNewSource({...newSource, type: e.target.value})}
              >
                <option value="sam_gov">SAM.gov (USA)</option>
                <option value="tenders_electronic_daily">TED (EU)</option>
                <option value="canadabuys">CanadaBuys</option>
                <option value="custom_api">Custom API</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">API Key (Optional)</label>
              <Input 
                type="password"
                placeholder="Enter API Key if required" 
                value={newSource.api_key}
                onChange={(e) => setNewSource({...newSource, api_key: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAddSource}>Connect Source</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sources;
